import {
  Archive,
  BadgeCheck,
  BarChart3,
  BellRing,
  LayoutDashboard,
  EyeOff,
  FileQuestion,
  FolderKanban,
  GalleryHorizontalEnd,
  HardDrive,
  Home,
  Link2,
  LogOut,
  Settings,
  ShieldCheck,
  Sparkles,
  UploadCloud,
  Users,
} from "lucide-react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { logout } from "../../lib/api";
import { clearDemoAuthRole } from "../../lib/runtimeMode";
import { BrandMark } from "./BrandMark";

const clientMenu = [
  { label: "Client Portal", to: "/client/portal", icon: LayoutDashboard },
  { label: "Memory Media Vault Dashboard", to: "/client/dashboard", icon: Home },
  { label: "Memory Media Vault", to: "/client/memory-hub/vault", icon: FolderKanban },
  { label: "Pending Review", to: "/client/pending", icon: BellRing },
  { label: "Approved Gallery", to: "/client/approved", icon: BadgeCheck },
  { label: "Featured on Memory Hub", to: "/client/featured", icon: Sparkles },
  { label: "Hidden / Private", to: "/client/hidden", icon: EyeOff },
  { label: "Not Approved", to: "/client/not-approved", icon: FileQuestion },
  { label: "Guest Upload Link", to: "/client/upload-link", icon: Link2 },
  { label: "Storage & Billing", to: "/client/storage-billing", icon: HardDrive },
  { label: "Settings", to: "/client/settings", icon: Settings },
  { label: "Instructions", to: "/client/instructions", icon: Archive },
];

const adminMenu = [
  { label: "Admin Dashboard", to: "/admin/dashboard", icon: BarChart3 },
  { label: "Clients", to: "/admin/clients", icon: Users },
  { label: "Events", to: "/admin/events", icon: GalleryHorizontalEnd },
  { label: "Memory Media Vault", to: "/admin/memory-hub/vault", icon: FolderKanban },
  { label: "Media", to: "/admin/media", icon: UploadCloud },
  { label: "Storage Setup", to: "/admin/storage-setup", icon: HardDrive },
  { label: "Activity Log", to: "/admin/activity-log", icon: ShieldCheck },
  { label: "Settings", to: "/admin/settings", icon: Settings },
];

export function PortalLayout({ mode = "client" }: { mode?: "client" | "admin" }) {
  const navigate = useNavigate();
  const menu = mode === "admin" ? adminMenu : clientMenu;

  const onLogout = async () => {
    try {
      await logout();
    } finally {
      clearDemoAuthRole();
      navigate(mode === "admin" ? "/admin/login" : "/login");
    }
  };

  return (
    <div className="portal-shell min-h-screen text-ktd-rich">
      <aside className="portal-sidebar fixed inset-y-0 left-0 z-40 hidden w-80 overflow-y-auto border-r border-ktd-gold/20 px-5 py-6 text-white shadow-luxe xl:block">
        <BrandMark />
        <div className="vault-rail mt-8 rounded-lg border border-ktd-gold/20 bg-white/[0.04] p-4">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-ktd-softGold">
            {mode === "admin" ? "CollabRecover AR Solutions" : "Universal Client Portal"}
          </p>
          <p className="mt-2 text-sm leading-6 text-white/68">
            Brand-scoped client experiences for portals, records, messages, files, forms, and delivery tracking.
          </p>
        </div>
        <nav className="mt-7 grid gap-2" aria-label={mode === "admin" ? "Admin portal menu" : "Client portal menu"}>
          {menu.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={`${item.label}-${item.to}`}
                to={item.to}
                className={({ isActive }) =>
                  `focus-ring flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-bold transition ${
                    isActive
                      ? "bg-ktd-gold text-ktd-black shadow-gold"
                      : "border border-transparent text-white/72 hover:border-ktd-gold/20 hover:bg-white/[0.07] hover:text-white"
                  }`
                }
              >
                <Icon className="size-4" aria-hidden="true" />
                {item.label}
              </NavLink>
            );
          })}
        </nav>
        <button
          type="button"
          onClick={onLogout}
          className="focus-ring mt-8 flex w-full items-center justify-center gap-2 rounded-lg border border-white/12 px-4 py-3 text-sm font-bold text-white/74 transition hover:border-ktd-gold/50 hover:text-white"
        >
          <LogOut className="size-4" aria-hidden="true" />
          Sign Out
        </button>
      </aside>

      <div className="xl:pl-80">
        <header className="sticky top-0 z-30 border-b border-ktd-gold/20 bg-ktd-black/92 px-4 py-4 backdrop-blur-xl sm:px-6 xl:hidden">
          <div className="flex items-center justify-between gap-4">
            <BrandMark />
            <button
              type="button"
              onClick={onLogout}
              className="focus-ring rounded-lg bg-ktd-black px-4 py-2 text-sm font-bold text-ktd-softGold"
            >
              Sign Out
            </button>
          </div>
          <nav className="luxury-scrollbar mt-4 flex gap-2 overflow-x-auto pb-2" aria-label="Mobile portal menu">
            {menu.map((item) => (
              <NavLink
                key={`${item.label}-${item.to}`}
                to={item.to}
                className={({ isActive }) =>
                  `focus-ring whitespace-nowrap rounded-lg px-4 py-2 text-sm font-bold ${
                    isActive ? "bg-ktd-gold text-ktd-black" : "bg-white text-ktd-rich"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </header>
        <main className="min-h-screen px-4 py-6 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
